import { useState, useEffect, useCallback } from 'react';
import { Users, FileText, CreditCard, Euro, TrendingUp, CalendarDays } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useAuth } from '../../context/AuthContext';

export default function AdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const StatCard = ({ icon: Icon, title, value, color }) => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className="text-3xl font-bold">{value}</p>
          </div>
          <div className={`p-4 rounded-2xl ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="h-32 bg-muted rounded-xl animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold mb-2">Dashboard Admin</h1>
        <p className="text-muted-foreground">Vue d'ensemble de votre activité</p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={Users}
          title="Total Clients"
          value={stats?.total_clients || 0}
          color="bg-blue-100 text-blue-600 dark:bg-blue-900/30"
        />
        <StatCard
          icon={CreditCard}
          title="Abonnements Actifs"
          value={stats?.active_subscriptions || 0}
          color="bg-green-100 text-green-600 dark:bg-green-900/30"
        />
        <StatCard
          icon={FileText}
          title="Devis en Attente"
          value={stats?.pending_quotes || 0}
          color="bg-orange-100 text-orange-600 dark:bg-orange-900/30"
        />
        <StatCard
          icon={Euro}
          title="Revenu Total"
          value={`${stats?.total_revenue || 0}€`}
          color="bg-primary/10 text-primary"
        />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card data-testid="previous-quarter">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-muted-foreground" />
              <CardTitle className="text-base">Trimestre précédent</CardTitle>
            </div>
            <p className="text-sm text-muted-foreground">
              {stats?.previous_quarter?.start} – {stats?.previous_quarter?.end}
            </p>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{stats?.previous_quarter?.revenue || 0}€</p>
          </CardContent>
        </Card>

        <Card data-testid="current-quarter" className="border-primary/30">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              <CardTitle className="text-base">Trimestre actuel</CardTitle>
            </div>
            <p className="text-sm text-muted-foreground">
              {stats?.current_quarter?.start} – {stats?.current_quarter?.end}
            </p>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold text-primary">{stats?.current_quarter?.revenue || 0}€</p>
          </CardContent>
        </Card>

      </div>
    </div>
  );
}
